<template>
  <div class="ledger-invoices-page">

    <!-- ── Toolbar ───────────────────────────────────────────────── -->
    <v-card elevation="2" class="toolbar-card mb-3">
      <v-toolbar dense flat color="blue darken-3" dark height="44px">
        <v-icon class="mr-2">mdi-book-open-page-variant</v-icon>
        <v-toolbar-title class="subtitle-1 font-weight-medium">Ledger Invoices</v-toolbar-title>
        <v-spacer />
        <v-chip small outlined color="white" class="mr-3">
          {{ invoices.length }} record{{ invoices.length !== 1 ? 's' : '' }}
        </v-chip>
        <v-btn icon dark title="Refresh" @click="loadInvoices">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Filters row -->
      <v-container fluid class="pa-3 pb-2">
        <v-row dense align="center">
          <!-- Search -->
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="search"
              label="Search invoice #, customer…"
              prepend-inner-icon="mdi-magnify"
              dense
              outlined
              clearable
              hide-details
              @keyup.enter="loadInvoices"
              @click:clear="onClearSearch"
            />
          </v-col>

          <!-- Date From -->
          <v-col cols="6" sm="3">
            <v-menu
              v-model="menuFrom"
              :close-on-content-click="false"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="startDate"
                  label="Date From"
                  prepend-inner-icon="mdi-calendar-start"
                  dense
                  outlined
                  readonly
                  clearable
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="startDate = null"
                />
              </template>
              <v-date-picker
                v-model="startDate"
                no-title
                @input="menuFrom = false"
              />
            </v-menu>
          </v-col>

          <!-- Date To -->
          <v-col cols="6" sm="3">
            <v-menu
              v-model="menuTo"
              :close-on-content-click="false"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="endDate"
                  label="Date To"
                  prepend-inner-icon="mdi-calendar-end"
                  dense
                  outlined
                  readonly
                  clearable
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="endDate = null"
                />
              </template>
              <v-date-picker
                v-model="endDate"
                no-title
                @input="menuTo = false"
              />
            </v-menu>
          </v-col>

          <!-- Apply / Clear / Export -->
          <v-col cols="12" sm="3" class="d-flex align-center flex-wrap" style="gap:6px;">
            <v-btn color="blue darken-3" dark depressed small @click="loadInvoices" :loading="isLoading">
              <v-icon left small>mdi-filter</v-icon>Apply
            </v-btn>
            <v-btn text small color="grey darken-1" @click="clearFilters">Clear</v-btn>
            <v-btn color="teal darken-2" dark depressed small @click="exportData" title="Export to CSV">
              <v-icon left small>mdi-download</v-icon>Export
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <!-- ── Error alert ────────────────────────────────────────────── -->
    <v-alert v-if="errorMsg" type="error" dense dismissible class="mb-3" @input="errorMsg = null">
      {{ errorMsg }}
    </v-alert>

    <!-- ── Grid ───────────────────────────────────────────────────── -->
    <v-card elevation="2">
      <ag-grid-vue
        style="width:100%;"
        class="ag-theme-balham"
        dom-layout="autoHeight"
        :column-defs="columnDefs"
        :default-col-def="defaultColDef"
        :row-data="invoices"
        :pagination="true"
        :pagination-page-size="50"
        row-selection="single"
        :loading-overlay-component-params="{ loadingMessage: 'Loading invoices…' }"
        :overlay-loading-template="loadingTemplate"
        @cell-clicked="onCellClicked"
        @row-double-clicked="onRowDoubleClicked"
        @grid-ready="onGridReady"
      />
    </v-card>

  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue'
import { ledgerService } from '../../services/'
import { EventBus } from '../../eventbus/event-bus.js'
import moment from 'moment'

export default {
  name: 'LedgerInvoicesPage',

  components: { AgGridVue },

  data: () => ({
    invoices: [],
    isLoading: false,
    errorMsg: null,
    search: '',
    startDate: null,
    endDate: null,
    menuFrom: false,
    menuTo: false,
    gridApi: null,
    loadingTemplate: '<span class="ag-overlay-loading-center">Loading ledger invoices…</span>',

    defaultColDef: {
      resizable: true,
      sortable: true,
      filter: true,
      minWidth: 80,
    },

    columnDefs: [
      {
        headerName: 'Inv #',
        field: 'id',
        width: 85,
        pinned: 'left',
        cellRenderer: p => `<a href="javascript:void(0)" style="color:#1565C0;font-weight:700;text-decoration:underline;cursor:pointer;" title="Click to open Invoice #${p.value}">#${p.value}</a>`
      },
      {
        headerName: 'Date',
        field: 'createdAt',
        width: 120,
        sort: 'asc',
        comparator: (d1, d2) => new Date(d1).getTime() - new Date(d2).getTime(),
        valueFormatter: p => p.value ? moment(p.value).format('DD-MM-YYYY') : ''
      },
      { headerName: 'Customer',       field: 'customer.name',           minWidth: 150, flex: 1 },
      { headerName: 'Contact',        field: 'customer.contactNumber',   width: 120 },
      { headerName: 'Passengers',     field: 'description',             minWidth: 140 },
      {
        headerName: 'Net Cost (£)',
        field: 'cost',
        width: 110,
        valueFormatter: p => p.value != null ? parseFloat(p.value).toFixed(2) : '0.00',
        cellStyle: { textAlign: 'right' }
      },
      {
        headerName: 'Gross (£)',
        field: 'total',
        width: 110,
        valueFormatter: p => p.value != null ? parseFloat(p.value).toFixed(2) : '0.00',
        cellStyle: { textAlign: 'right' }
      },
      {
        headerName: 'Posted',
        field: 'isPosted',
        width: 85,
        cellRenderer: p => p.value
          ? '<span style="color:#2e7d32;font-weight:600;">✔ Yes</span>'
          : '<span style="color:#e65100;">✘ No</span>'
      },
      { headerName: 'Created By',     field: 'User.name',               width: 120 },
    ],
  }),

  mounted () {
    // Keep empty on initial open — only load when user searches or selects filters
  },

  methods: {
    loadInvoices () {
      const hasSearch = this.search && this.search.trim()
      const hasDate = this.startDate || this.endDate
      if (!hasSearch && !hasDate) {
        this.errorMsg = 'Please enter a search term or select a date range to load invoices.'
        return
      }

      this.isLoading = true
      this.errorMsg = null
      ledgerService.getLedgerInvoices(this.startDate, this.endDate, this.search)
        .then(data => {
          this.invoices = data || []
          if (!this.invoices.length) {
            this.errorMsg = 'No matching invoices found.'
          }
        })
        .catch(err => {
          console.error(err)
          this.errorMsg = 'Failed to load ledger invoices. Please try again.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    clearFilters () {
      this.search = ''
      this.startDate = null
      this.endDate = null
      this.invoices = []
      this.errorMsg = null
    },

    onClearSearch () {
      this.search = ''
      if (this.startDate || this.endDate) {
        this.loadInvoices()
      } else {
        this.invoices = []
      }
    },

    onGridReady (params) {
      this.gridApi = params.api
    },

    onCellClicked (event) {
      if (event.colDef && event.colDef.field === 'id' && event.data && event.data.id) {
        this.openInvoice(event.data.id)
      }
    },

    onRowDoubleClicked (event) {
      if (event.data && event.data.id) {
        this.openInvoice(event.data.id)
      }
    },

    openInvoice (invoiceId) {
      if (!invoiceId) return
      this.$store.dispatch('invoice/getInvoice', invoiceId)
      this.$router.push('/invoices').catch(() => {})
      this.$nextTick(() => {
        EventBus.$emit('loadInvoiceById', invoiceId)
      })
      setTimeout(() => {
        EventBus.$emit('loadInvoiceById', invoiceId)
      }, 150)
      setTimeout(() => {
        EventBus.$emit('loadInvoiceById', invoiceId)
      }, 400)
    },

    exportData () {
      if (!this.invoices || this.invoices.length === 0) {
        this.errorMsg = 'No invoices available to export.'
        return
      }

      const headers = ['Inv #', 'Date', 'Customer', 'Contact', 'Passengers', 'Net Cost (£)', 'Gross (£)', 'Posted', 'Created By']
      const rows = this.invoices.map(inv => [
        inv.id,
        inv.createdAt ? moment(inv.createdAt).format('DD-MM-YYYY') : '',
        `"${((inv.customer && inv.customer.name) || '').replace(/"/g, '""')}"`,
        `"${((inv.customer && inv.customer.contactNumber) || '').replace(/"/g, '""')}"`,
        `"${(inv.description || '').replace(/"/g, '""')}"`,
        inv.cost != null ? parseFloat(inv.cost).toFixed(2) : '0.00',
        inv.total != null ? parseFloat(inv.total).toFixed(2) : '0.00',
        inv.isPosted ? 'Yes' : 'No',
        `"${((inv.User && inv.User.name) || '').replace(/"/g, '""')}"`
      ])

      const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + [headers.join(','), ...rows.map(r => r.join(','))].join('\r\n')
      const encodedUri = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', encodedUri)
      link.setAttribute('download', `ledger_invoices_${moment().format('YYYY-MM-DD')}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    formatDate (val) {
      return val ? moment(val).format('DD-MM-YYYY') : '—'
    },

    formatNum (val) {
      const n = parseFloat(val)
      return isNaN(n) ? '0.00' : n.toFixed(2)
    },
  },
}
</script>

<style scoped>
.ledger-invoices-page {
  padding: 12px 16px;
}
.toolbar-card {
  border-radius: 8px;
  overflow: hidden;
}
</style>
